import { Subscription } from 'rxjs';
import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { AdsPrismGISAddEditComponent } from '../ads-prism-gis-add-edit/ads-prism-gis-add-edit.component';
import { GISService } from 'app/shared/services/gis-service';
import { GISLayerMapping, GisServiceList, ServiceTypes, GetArcGISUserTokenRequest } from 'app/shared/models/gis-service-list';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { customerEditorQueryParam, customerQueryParam } from 'app/shared/models/customer';
import { DomOperationUtilsService } from 'app/shared/utils/dom-operation-utils.service';
import { ArcGISUserService } from 'app/shared/services/arcgis-user-service';
import { GIS_SERVICES_LIST } from 'app/main/main-resolver.service';
import { normalizePortalUrl } from 'app/shared/constants/arcgis-constants';
import { TranslateService } from '@ngx-translate/core';
import { IComponentModalResult } from 'app/shared/models/modal';
@Component({
    selector: 'ads-prism-gis-layers',
    templateUrl: './ads-prism-gis-layers.component.html',
    styleUrls: ['./ads-prism-gis-layers.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdsPrismGISLayersComponent implements OnInit, OnDestroy {
    public mapTypeDisplayNames = { maps: 'MapServer', feat: 'FeatureServer', identity: 'ArcGIS Identity' };

    public assignedRainGauge = new Array<Location>();
    public searchString = new FormControl();
    public gisDescriptionColumns = [
        'type',
        'url',
        'action',
    ];
    public gisDataSource: GisServiceList[];
    public isLoading = false;

    public customerId: number;
    private globalCustomerId: number;

    public gisLayerMapping: GISLayerMapping[];
    public gisServiceList: GisServiceList;

    private addDialogRef: MatDialogRef<AdsPrismGISAddEditComponent>;
    private subscriptions: Subscription[] = [];
    constructor(
        private dialog: MatDialog,
        private GISServices: GISService,
        private activeRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private snackBar: MatSnackBar,
        private uiUtilsService: UiUtilsService,
        private domOperationUtilsService: DomOperationUtilsService,
        private arcgisUserService: ArcGISUserService,
        private translate: TranslateService,
    ) {}

    public ngOnInit() {
        this.subscriptions.push(this.domOperationUtilsService.gisDialogOpened.subscribe((shouldOpen: boolean) => {
            if (!this.addDialogRef && shouldOpen) {
                this.addNewGISService();
            } else if (this.addDialogRef && !shouldOpen) {
                this.addDialogRef.close();
                this.addDialogRef = null;
            }
        }));
        // get current query params for customer
        this.customerId = Number(this.activeRoute.snapshot.queryParamMap.get(customerEditorQueryParam));
        this.globalCustomerId = Number(this.activeRoute.snapshot.queryParamMap.get(customerQueryParam));
        this.getLayers();
    }

    private get isEditingGlobalCustomer(): boolean {
        return this.customerId === this.globalCustomerId;
    }

    public getLayers(): void {
        this.isLoading = true;
        this.uiUtilsService.safeChangeDetection(this.cdr);
        this.GISServices.getGISServicesList(this.customerId).subscribe((res: GisServiceList[]) => {
            if (res != null) {
                this.gisDataSource = res;
                if (this.isEditingGlobalCustomer) {
                    sessionStorage.removeItem(GIS_SERVICES_LIST);
                    sessionStorage.setItem(GIS_SERVICES_LIST, JSON.stringify(this.gisDataSource));
                    this.arcgisUserService.gisServicesListChanged.next(this.gisDataSource);
                    const identityService = this.gisDataSource?.find(s => s.serviceType === ServiceTypes.IDENTITY);
                    if (identityService) {
                        this.getArcGISUserToken(identityService);
                    }
                }
            } else {
                this.gisDataSource = [];
            }
            this.isLoading = false;
            this.uiUtilsService.safeChangeDetection(this.cdr);
        }, () => {
            this.gisDataSource = [];
            this.isLoading = false;
            this.uiUtilsService.safeChangeDetection(this.cdr);
        });
    }

    private getArcGISUserToken(identityService: GisServiceList): void {
        const enterpriseUrl: string | null = identityService.url ? normalizePortalUrl(identityService.url) : null;
        const req: GetArcGISUserTokenRequest = {
            arcGISEnterpriseDomainUrl: enterpriseUrl,
            arcGISEnterpriseAppId: enterpriseUrl ? identityService.appId ?? null : null,
            customerId: enterpriseUrl ? this.customerId : undefined,
        };
        this.arcgisUserService.getUserCredToken(req);
    }

    public addNewGISService() {
        const hasIdentityConfig: boolean = this.gisDataSource?.some(s => s.serviceType === ServiceTypes.IDENTITY) ?? false;

        this.addDialogRef = this.dialog
            .open(AdsPrismGISAddEditComponent, {
                disableClose: true,
                hasBackdrop: false,
                data: { hasIdentityConfig } as GisServiceList,
                panelClass: 'ads-prism-gis-add-edit'
            });

        this.domOperationUtilsService.gisDialogOpened.next(true);
        this.addDialogRef.afterClosed().subscribe((result: IComponentModalResult | undefined) => {
            this.addDialogRef = null;
            this.domOperationUtilsService.gisDialogOpened.next(false);
            if (result?.success) {
                this.showChangesSavedToast();
            }
            this.getLayers();
        });
    }

    public deleteGISService(id: number) {
        this.GISServices.deleteGISservice(this.customerId, id).subscribe((res) => {
            if (res && res.isError) {
                this.snackBar.open('Error!', 'Technical error', {
                    panelClass: 'custom-error-snack-bar',
                });
            } else {
                this.showChangesSavedToast();
                this.gisDataSource = this.gisDataSource.filter((o) => o.serviceId !== id);
                if (this.isEditingGlobalCustomer) {
                    sessionStorage.removeItem(GIS_SERVICES_LIST);
                    this.GISServices.getGISServicesList(this.customerId).subscribe((services: GisServiceList[]) => {
                        sessionStorage.setItem(GIS_SERVICES_LIST, JSON.stringify(services));
                        this.arcgisUserService.gisServicesListChanged.next(services);
                        const identityService = services?.find(s => s.serviceType === ServiceTypes.IDENTITY);
                        if (identityService) {
                            this.getArcGISUserToken(identityService);
                        }
                        else {
                            this.arcgisUserService.arcGISUserTokenChanged.next(null);
                        }
                    });
                }
            }

            this.uiUtilsService.safeChangeDetection(this.cdr);
        });
    }

    public editGISService(element: GisServiceList) {
        const hasIdentityConfig: boolean = this.gisDataSource?.some(s => s.serviceType === ServiceTypes.IDENTITY) ?? false;

        this.addDialogRef = this.dialog
            .open(AdsPrismGISAddEditComponent, {
                disableClose: true,
                hasBackdrop: false,
                data: { ...element, hasIdentityConfig },
            });

        this.domOperationUtilsService.gisDialogOpened.next(true);
        this.addDialogRef.afterClosed().subscribe((result: IComponentModalResult | undefined) => {
            this.addDialogRef = null;
            this.domOperationUtilsService.gisDialogOpened.next(false);
            if (result?.success) {
                this.showChangesSavedToast();
            }
            this.getLayers();
        });
    }

    private showChangesSavedToast(): void {
        this.snackBar.open(
            this.translate.instant('CUSTOMER_EDITOR.CHANGES_SAVED_TO_CUSTOMER'),
            this.translate.instant('COMMON.DISMISS_TEXT'),
            { duration: 10000 },
        );
    }

    ngOnDestroy(): void {
        for(const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        this.subscriptions = [];
    }
}
